import type { ProjectionVariant, YearProjection } from './projection-chart';

import { Icon, Separator, Typography } from '@polyseme/design-system';
import { cn } from '@polyseme/utils/cn/cn-utils';
import { formatCurrency } from '@polyseme/utils/number/number-utils';
import { cva } from 'class-variance-authority';
import { memo } from 'react';

type TooltipItemProps = {
  blurred?: boolean;
  className?: string;
  label: string;
  textColor?: string;
  value: number;
};

const TooltipItem = ({
  blurred = false,
  className,
  label,
  textColor = 'text-gray-800',
  value,
}: TooltipItemProps) => (
  <dl className={cn('flex flex-col justify-center gap-1', className)}>
    <dt className="line-clamp-3 whitespace-pre-wrap text-sm">{label}</dt>
    <dd
      className={cn(
        'relative flex flex-col text-base font-bold',
        blurred && 'select-none blur-sm'
      )}
    >
      <Typography
        as="span"
        className={cn('hidden md:inline-block', textColor)}
        variant="title-5"
      >
        {formatCurrency(value)}
      </Typography>
      <Typography
        aria-hidden="true"
        as="span"
        className="hidden md:invisible md:inline-block md:h-0" // Avoid layout shift when value updates
        variant="title-5"
      >
        {formatCurrency(9_999_999)}
      </Typography>
      <Typography as="span" className={cn('md:hidden', textColor)}>
        {formatCurrency(value, {
          isCompact: true,
        })}
      </Typography>
    </dd>
  </dl>
);

export type ChartSummaryTooltipLabels = {
  investmentMade: string;
  interestGenerated: string;
  totalSavings: string;
  totalSavingsAtLastYear: string;
};

type ChartSummaryTooltipProps = YearProjection & {
  blurAmounts?: boolean;
  isLastYear?: boolean;
  variant: ProjectionVariant;
  labels: ChartSummaryTooltipLabels;
};

const variants = cva(
  'w-full rounded-xl border bg-background p-3 text-center text-black md:min-h-24 md:w-auto',
  {
    variants: {
      variant: {
        default: 'mb-6 inline-block',
        floating:
          'flex flex-col gap-4 bg-white lg:absolute lg:left-0 lg:top-0 lg:z-10',
      },
    },
  }
);

export const ChartSummaryTooltip = memo(
  ({
    blurAmounts = false,
    investmentMade,
    isLastYear = false,
    labels,
    median,
    variant,
    year,
  }: ChartSummaryTooltipProps) => {
    const interestGenerated = median - investmentMade;
    const totalSavings = investmentMade + interestGenerated;

    const totalSavingsLabel = isLastYear
      ? labels.totalSavingsAtLastYear
      : `${labels.totalSavings} ${year}`;

    return (
      <div className={cn(variants({ variant }))}>
        {variant === 'floating' && (
          <div className="flex flex-col gap-4">
            <Typography
              className="text-left"
              variant="title-7"
            >{`${labels.totalSavings} ${year}`}</Typography>
            <Separator />
          </div>
        )}
        <div
          className={cn(
            'grid grid-cols-[1fr_auto_1fr] gap-4 md:flex md:self-start',
            {}
          )}
        >
          <TooltipItem
            blurred={blurAmounts}
            label={labels.investmentMade}
            value={investmentMade}
          />

          <div className="grid w-full place-items-center md:w-auto">
            <Icon name="plus" />
          </div>

          <TooltipItem
            blurred={blurAmounts}
            label={labels.interestGenerated}
            value={interestGenerated}
          />

          <div className="col-span-full flex items-center justify-center gap-2 lg:col-span-1">
            <Separator className="flex-1 md:hidden" />
            <Icon name="equal" />
            <Separator className="flex-1 md:hidden" />
          </div>

          <TooltipItem
            blurred={blurAmounts}
            className="col-span-full md:col-span-1"
            label={
              variant === 'floating'
                ? labels.totalSavingsAtLastYear
                : totalSavingsLabel
            }
            textColor="text-primary"
            value={totalSavings}
          />
        </div>
      </div>
    );
  }
);

ChartSummaryTooltip.displayName = 'ChartSummaryTooltip';
